// Phase 43 — <LiveFillCanvas /> (Plan 43-05 Task 1, CANVAS-01).
// Renders inside a right-side Sheet matching the legacy AddBoatWizard
// pattern (1320px floating card, inset-5 from viewport edges) so the
// wizard feels consistent with the rest of the app. The original
// "full-page route shell" approach from UI-SPEC §Layout Contract (D-02)
// was reverted after dealer feedback — the takeover lacked the modal
// affordance dealers expect.
// Three-column desktop grid (200px / 1fr / 340px) + responsive reflow:
//   tablet/phone use a sticky top section dropdown + bottom-sheet copilot FAB.
//
// Hook plumbing: useCreateBoat → useWizardDraftSync → useUnifiedMediaUpload →
// useWizardAI → useDealershipSettings → useCanvasHydration → useCanvasAutoSave.
// The per-step status hook is replaced with useCanvasNav and the wizard's
// navigation hook is dropped (flat scroll has no "current step").
//
// #1178 / #1184 (spec §2) — form VALUES come only from the server's merged boat,
// through useCanvasHydration: a fresh read on mount (new and resumed canvases
// alike), on phase_done and the run terminal, after a reconnect and every 15 s
// while a run is in progress. Streamed field events feed only the Copilot
// progress text. `_provenance` is applied separately by the provenance effect
// below from GET /boats/{id}/field-sources; it never touches values.
//
// #1177 (spec §3.1) — every save (autosave, Save draft, Create) sends only the
// dealer's changes with `version` (useCanvasAutoSave → useBoatSave).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@marine/ui/button';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@marine/ui/sheet';
import { useCreateBoat } from '@/modules/inventory/hooks/useBoats';
import { useCreatePendingVocabularyTerms } from '@/modules/inventory/hooks/useCreatePendingVocabularyTerms';
import { useBoatMedia, boatMediaKeys } from '@/modules/inventory/hooks/useBoatMedia';
import type { BoatMediaResponse } from '@/modules/inventory/services/boatMediaApi';
import { useDealershipSettings } from '@/modules/crm/hooks/useDealershipSettings';
import { useMarketBand } from '@/modules/inventory/hooks/useMarketBand';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import type {
  InventoryMediaItem,
  Location,
} from '@/modules/inventory/types/boat.types';
import type { TempMediaItem } from './types';
import {
  WIZARD_DEFAULT_VALUES,
  type WizardFormData,
} from '@/modules/inventory/components/boat-form/types/wizard.types';
import { useWizardDraftSync } from '@/modules/inventory/components/boat-form/hooks/useWizardDraftSync';
import { useWizardAI } from '@/modules/inventory/components/boat-form/hooks/useWizardAI';
import { useUnifiedMediaUpload } from '@/modules/inventory/components/boat-form/hooks/useUnifiedMediaUpload';
import {
  useCombinedUpload,
  type CombinedUploadResult,
} from '@/modules/inventory/components/documents/hooks/useCombinedUpload';
import { useDocuments, documentKeys } from '@/modules/inventory/components/documents/hooks/useDocuments';
import { toast } from 'sonner';
import { CanvasSectionNav, CANVAS_SECTION_IDS } from './CanvasSectionNav';
import { CanvasHeroIngest } from './CanvasHeroIngest';
import { CanvasFormSections } from './CanvasFormSections';
import { useCanvasNav } from './hooks/useCanvasNav';
import { useCanvasBoatActions } from './hooks/useCanvasBoatActions';
import { aiApi } from '@/modules/inventory/services/aiApi';
import { useLocations } from '@/modules/inventory/hooks/useLocations';
import { buildAllActiveLocationDefaults } from '@/modules/inventory/components/boat-form/utils/locationDefaults';
import { useFactoryPacketExtract } from './hooks/useFactoryPacketExtract';
import { useCanvasHydration, type CanvasRunEvent } from './hooks/useCanvasHydration';
import { useCanvasAutoSave, type CanvasConflict } from './hooks/useCanvasAutoSave';
import { useCanvasFieldReview } from './hooks/useCanvasFieldReview';
import { CanvasReviewContext, type CanvasReviewValue } from './CanvasReview';
import { toFormScalar, type CanvasScalarKey } from './lib/canvasEditable';
import { ChangedElsewhereDialog } from '@/modules/inventory/components/save/ChangedElsewhereDialog';
import { applyConflictChoices, type ConflictChoice } from '@/modules/inventory/utils/boat-changes';
import { describeLostRow } from '@/modules/inventory/utils/boat-change-labels';
import { CopilotSidebar } from './CopilotSidebar';
import { CopilotSessionProvider } from './CopilotSessionContext';

// Phase 45.5.3 (review) — map a server InventoryMediaItem (from the useBoatMedia
// query, the SINGLE source of truth for committed gallery media) into the
// committed MediaCard shape (TempMediaItem) the hero grid renders. The field
// mapping mirrors useUnifiedMediaUpload.loadExisting verbatim (clientId
// `existing-<id>`, empty tempMediaId since the row is already in
// inventory_images, card→list→url conversion preference) so committed cards look
// identical whether the canvas sourced them from the query or the legacy hook.
// Pure + module-local so it is referentially stable for the useMemo below.
function mapQueryImageToCommittedCard(img: InventoryMediaItem): TempMediaItem {
  return {
    clientId: `existing-${img.id}`,
    tempMediaId: '',
    mediaId: img.id,
    url: img.conversions?.card ?? img.conversions?.list ?? img.url,
    fileName: img.file_name,
    size: img.size ?? 0,
    uploading: false,
    progress: 100,
  };
}

interface LiveFillCanvasProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumeDraftId?: string;
  onSuccess?: (boatId: string) => void;
  // Phase 45.5.2 — explicit "blank-new entry" signal for callers that open the
  // canvas as a layout-mounted MODAL (e.g. QuickAddHost's top-bar "+ → Add
  // Boat") rather than via the route that sets `?new` (BoatsPageHeader). Without
  // it, `showHeroIngest` keys only off `?new`/draftId/resumeDraftId — none of
  // which a fresh modal has — so the hero ingest dropzone never renders.
  newEntry?: boolean;
}

export function LiveFillCanvas({
  open,
  onOpenChange,
  resumeDraftId,
  onSuccess,
  newEntry,
}: LiveFillCanvasProps) {
  const methods = useForm<WizardFormData>({
    defaultValues: WIZARD_DEFAULT_VALUES,
    mode: 'onTouched',
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        // Phase 45.5.3 (refresh) — slightly responsive width. Fills the
        // viewport minus the inset-5 gutters (2 × 1.25rem = 2.5rem) and is
        // CAPPED at 1320px so it shrinks gracefully on smaller desktops instead
        // of overflowing, while never growing past the legacy 1320 on ultrawide
        // monitors. `sm:max-w-[1320px]` (an arbitrary value at the `sm`
        // breakpoint) both supplies that cap AND overrides the sheet base
        // variant's `sm:max-w-sm` (24rem) which would otherwise clamp the width.
        // The <1280px stacked reflow (driven by the inner grid's useMediaQuery)
        // is unchanged.
        className="gap-0 w-[calc(100vw-2.5rem)] max-w-[1320px] sm:max-w-[1320px] inset-5 border start-auto h-auto rounded-lg p-0 [&_[data-slot=sheet-close]]:top-4.5 [&_[data-slot=sheet-close]]:end-5"
        side="right"
      >
        <FormProvider {...methods}>
          <LiveFillCanvasInner
            methods={methods}
            onOpenChange={onOpenChange}
            onSuccess={onSuccess}
            resumeDraftId={resumeDraftId}
            newEntry={newEntry}
          />
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
}

interface LiveFillCanvasInnerProps {
  methods: ReturnType<typeof useForm<WizardFormData>>;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (boatId: string) => void;
  resumeDraftId?: string;
  newEntry?: boolean;
}

function LiveFillCanvasInner({
  methods,
  onOpenChange,
  onSuccess,
  resumeDraftId,
  newEntry,
}: LiveFillCanvasInnerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase 45.5.3 (review) — the hero ingest surface (and, critically, the
  // query-backed committed MediaCard grid it hosts) renders on BOTH entry
  // points: blank-new (`?new`) AND resume (`?new&resume=<id>`).
  //
  // It used to be gated on `has('new') && !has('resume')`, which is FALSE on
  // resume — so when a dealer reopened a draft, the committed gallery (sourced
  // from useBoatMedia(draftSync.boatId)) had no render surface and the draft's
  // saved photos were invisible, even though the query fetched them. That broke
  // the design's "saved images get the v1 thumbnail + ⭐ treatment too" intent.
  //
  // The hero already self-adapts per entry point: MediaCardGrid returns null
  // when both staged and committed sources are empty (so a brand-new draft with
  // no media adds no grid chrome), and the dropzone/"Start your listing" prompt
  // stays available + per-draftId dismissible so a resuming dealer can still add
  // more files or hide the prompt.
  //
  // `showHeroIngest` is derived AFTER draftSync below so it can also key off a
  // live draft id (see the derivation past the hook plumbing).

  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 1280px)');

  // Phase 45.5.3 (CANVAS-16) — the LIFTED copilot collapse boolean. Previously
  // CopilotSidebar owned this internally, but the grid's third track was
  // HARDCODED at 340px, so collapsing left a dead 340px gap. Lifting the boolean
  // here lets the grid template shrink 340px→48px in lock-step with the rail
  // (RESEARCH "the only genuinely new wiring is … lifting one boolean"). This is
  // now the SINGLE owner of the `canvas-copilot-collapsed` localStorage key —
  // CopilotSidebar's internal persistence stands down when `collapsed` is passed.
  const [copilotCollapsed, setCopilotCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('canvas-copilot-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('canvas-copilot-collapsed', String(copilotCollapsed));
    } catch {
      /* localStorage may be unavailable in some test envs */
    }
  }, [copilotCollapsed]);

  // Mirror AddBoatWizard's inner hook plumbing order (D-16) ----------------
  const { isPending: isCreating, mutateAsync: createBoat } = useCreateBoat();
  // E5c — persists genuinely-new evolving controlled-vocab values (fuel_type /
  // engine_type) as provisional catalog terms when the boat is saved. Invoked
  // inside useCanvasBoatActions.handleCreate before the boat write.
  const createPendingVocabularyTerms = useCreatePendingVocabularyTerms();
  const draftSync = useWizardDraftSync();

  // A3 — pre-select all active locations for a blank canvas once they load. A
  // resumed canvas, or one whose draft already exists, takes the boat's own
  // locations from useCanvasHydration. On a brand-new draft the seeded selection
  // is the dealer's and the first autosave saves it (decision D43).
  const { data: activeLocations } = useLocations({ is_active: true });
  const seededLocationsRef = useRef(false);
  useEffect(() => {
    if (seededLocationsRef.current || resumeDraftId || draftSync.boatId) return;
    const list: Location[] = activeLocations ?? [];
    if (list.length === 0) return;
    if ((methods.getValues('location_ids') ?? []).length > 0) return;
    const defaults = buildAllActiveLocationDefaults(list);
    methods.setValue('location_ids', defaults.location_ids, { shouldDirty: false });
    methods.setValue('primary_location_id', defaults.primary_location_id, { shouldDirty: false });
    seededLocationsRef.current = true;
  }, [activeLocations, methods, resumeDraftId, draftSync.boatId]);

  // Phase 45.5.3 — render the hero on BOTH entry points (blank-new `?new` AND
  // resume `?new&resume=<id>`). Today every resume URL carries `?new`
  // (BoatsPageHeader sets `?new&resume=<id>`), so `has('new')` alone already
  // covers resume — but ALSO keying off a live draft id (draftSync.boatId set by
  // resume hydration / ensureDraft, or the resumeDraftId prop) is defensive: it
  // guarantees the query-backed committed media is never invisible if a resume
  // ever opens via the prop without `?new`. The grid self-hides when empty, so
  // this never adds chrome to a media-less blank-new canvas.
  const showHeroIngest =
    newEntry || searchParams.has('new') || !!draftSync.boatId || !!resumeDraftId;

  const mediaUpload = useUnifiedMediaUpload();
  const wizardAI = useWizardAI({ boatId: draftSync.boatId });
  const { data: dealershipSettings } = useDealershipSettings();

  // Per-step status + step-navigation hooks from the wizard are intentionally
  // omitted (D-01) — flat scroll uses a section-spy hook instead.
  const { activeSection, jumpTo } = useCanvasNav(CANVAS_SECTION_IDS);

  // Phase 44 carryover Task 2 — single shared useMarketBand instance.
  // Lifted here (inside FormProvider) so both the "Suggest price" button on
  // the pricing field AND the post-Comparables CTA in CopilotFeed drive the
  // same job/result state. FormProvider is mounted by the outer LiveFillCanvas
  // shell, so useFormContext + useProvenance('our_price') inside useMarketBand
  // resolve correctly at this call site.
  const marketBand = useMarketBand(draftSync.boatId ?? null);

  // Phase 45.5 — factory-packet extraction (INGEST-01). useFactoryPacketExtract
  // owns the "Confirm & extract →" trigger and the optimistic "now reading…"
  // state. Form values arrive only through useCanvasHydration (#1178); the
  // Copilot progress and narrative are CopilotFeed's (useCopilotSession, by boatId).
  const factoryPacket = useFactoryPacketExtract();
  const tenantId = dealershipSettings?.tenant_id ?? null;
  // Phase 45.5.3 (review) — the canvas committed-media grid is sourced from the
  // SAME live query v1's BoatMediaManager uses (`useBoatMedia(boatId).data.images`),
  // making the React-Query cache the SINGLE source of truth. The query is
  // `enabled: !!boatId`, so it no-ops on a blank-new canvas with no draft yet.
  // Every committed mutation (delete / set-primary, fired by MediaCard) already
  // invalidates boatMediaKeys.detail(boatId), so the grid SELF-HEALS on the
  // refetch — the deleted card disappears, the ⭐ follows the new primary — with
  // NO local snapshot to hand-reconcile (this replaces the removed CR-01/CR-02
  // band-aid). On resume the query fetches automatically once boatId is set.
  const boatMedia = useBoatMedia(draftSync.boatId ?? '');
  const queryClient = useQueryClient();

  // Phase 45.5.2 sub-plan 5 — provenance now comes from inventory_field_sources
  // (dedicated projection), NOT ai_meta. Covers new + legacy drafts (sub-plan 1
  // backfilled legacy ai_meta._provenance into field_sources).
  const { data: fieldSources } = useQuery({
    queryKey: ['field-sources', draftSync.boatId],
    queryFn: () => aiApi.getFieldSources(draftSync.boatId as string),
    enabled: Boolean(draftSync.boatId),
    // Review Minor 14 — the global staleTime is 5 min; a reopened canvas must not
    // show a cached projection (flags and tints change with every merge).
    staleTime: 0,
  });

  // #1219 — a run moves media between collections (a held image into the
  // gallery, a photographed page into documents), so refresh both grid sources
  // when a phase or the run ends.
  const refreshMediaLists = useCallback(() => {
    if (!draftSync.boatId) return;
    queryClient.invalidateQueries({ queryKey: boatMediaKeys.detail(draftSync.boatId) });
    queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
  }, [draftSync.boatId, queryClient]);

  // #1178 (spec §2) — useCanvasHydration re-reads the boat on phase_done and on
  // the run terminal. Here the canvas refreshes what else the merge moved
  // (provenance, the media grid, the documents list) and, on the terminal, ends
  // the hero's "now reading…" state. A live failure also says so.

  const onRunEvent = useCallback(
    (event: CanvasRunEvent) => {
      if (draftSync.boatId) {
        queryClient.invalidateQueries({ queryKey: ['field-sources', draftSync.boatId] });
      }
      refreshMediaLists();
      if (event.type === 'phase_done') return;
      factoryPacket.markReadingComplete();
      if (event.type === 'failed' && event.source === 'live') {
        toast.error(event.userMessage?.trim() || 'Document reading failed — please fill the fields manually or try again.');
      }
    },
    [draftSync.boatId, queryClient, refreshMediaLists, factoryPacket],
  );

  const hydration = useCanvasHydration({
    methods,
    boatId: draftSync.boatId,
    tenantId,
    runStarted: factoryPacket.isReading,
    onRunEvent,
  });

  // Phase 45.5.1 (CANVAS-08, D-1) — the SINGLE combined-upload instance, LIFTED
  // here so its bound boatId tracks the live draftSync.boatId across renders.
  // The canvas hero used to construct its own useCombinedUpload(draftId); that
  // bound the boatId at the hero's render and could not see a boat created
  // mid-drop by ensureDraft().
  // NOTE (CR-01/WR-02): lifting does NOT make the bind synchronous. ensureDraft()
  // calls draftSync.setBoatId internally, but that is async React state, so this
  // hook does not re-bind until the NEXT render — AFTER runInlineIngest's
  // same-tick commit. So the hero does NOT rely on the re-bind: it passes the
  // fresh boat id (and the just-dropped batch) explicitly into
  // upload.commit({ boatId, items }). The re-bind here only keeps later
  // interactions (reroute/remove/retry) bound to the right boat.
  // The hero receives this instance (+ ensureDraft) as props and renders the
  // inline UploadedFilesStrip from its `items` instead of the modal.
  //
  // #581 Task 9 — suppressExtract=true: the canvas drives extraction itself
  // (useFactoryPacketExtract, triggered explicitly after commit — see
  // CanvasHeroIngest's runInlineIngest), so this instance's commits must
  // suppress the backend's auto-trigger propose-mode extraction (#581 Task 5)
  // to avoid dispatching a second, competing extraction job for the same
  // upload.
  const upload = useCombinedUpload(draftSync.boatId, '/boats', true);

  // Derive the committed gallery cards from the query. Memoized on the images
  // array identity so the mapped list is stable between unrelated re-renders.
  const committedImages = boatMedia.data?.images;
  const committedItems = useMemo<TempMediaItem[]>(
    () => (committedImages ?? []).map(mapQueryImageToCommittedCard),
    [committedImages],
  );

  // BUG FIX (hardened primary derivation): the primary is the image with the
  // lowest `order` (order_column), NOT positionally images[0]. The /media query
  // returns images already sorted ascending by order_column, so images[0] HAPPENS
  // to be the primary today — but fix #3 seeds the boat-media cache from the
  // combined-upload result (a SEPARATE ordering path) before the refetch lands,
  // so relying on array position couples the ⭐ to whichever path filled the
  // cache. Deriving from the explicit `order` field (which both formatImages and
  // formatMedia emit) flags the correct card regardless of source array order.
  // Falls back to the first item when `order` is absent on every image (defensive
  // — matches the legacy images[0] behaviour for any pre-order payload).
  const committedPrimaryImageId = useMemo<number | undefined>(() => {
    if (!committedImages || committedImages.length === 0) return undefined;
    let primary = committedImages[0];
    for (const img of committedImages) {
      if ((img.order ?? Number.POSITIVE_INFINITY) < (primary.order ?? Number.POSITIVE_INFINITY)) {
        primary = img;
      }
    }
    return primary.id;
  }, [committedImages]);

  // Phase 45.5.3 (committed DOCUMENTS on resume) — THE BUG FIX. Committed images
  // load via useBoatMedia above, but BoatMediaResponse is { images, videos } with
  // NO documents, so a draft created by uploading a factory packet (COO / invoice
  // / BOL PDFs) showed NONE of its uploaded files on resume. Factory-packet
  // documents live in a SEPARATE store — useDocuments(boatId) → GET
  // /boats/{id}/documents — so source them here with the SAME self-healing query
  // pattern: the query is `enabled: !!boatId` (no-ops on a blank-new canvas), and
  // the destroy mutation invalidates documentKeys.lists() so the grid self-heals
  // on the refetch (no local snapshot, exactly like committed images). Threaded
  // into the hero as committedDocuments + an onDeleteDocument callback below.
  const documents = useDocuments(draftSync.boatId);
  const committedDocuments = documents.list.data ?? [];
  const destroyDocument = documents.destroy;

  // Phase 3B (Task 3.6) — the live per-file reading rows are now owned entirely
  // by CopilotFeed via useCopilotSession (subscribed by boatId), which creates
  // each row from its STATUS event's server media_id. The canvas no longer seeds
  // or threads any `extractionFiles` prop (the retired ExtractionStatusPanel's
  // client seed was already permanently empty and is gone).

  // Phase 45.5.2 (CANVAS-13 / CONTEXT D-09) — the "Re-run scorecard" imperative
  // handle (useScorecard.runNow) was removed alongside the ScorecardPanel /
  // CoachPanel surfaces in CopilotSidebar. The scorecard feature was then fully
  // removed (SP1): the hook, panels, and aiScorecardApi are deleted. No
  // scorecard subscription runs from the canvas now.

  // #1177 (spec §2, §3.1) — autosave sends only the dealer's changes: the
  // difference between the form and the last server snapshot. A re-read writes
  // the snapshot before the form, so hydration never produces a change to save.
  const [canvasConflict, setCanvasConflict] = useState<CanvasConflict | null>(null);
  const autoSave = useCanvasAutoSave({
    methods,
    boatId: draftSync.boatId,
    enabled: autoSaveEnabled,
    hydration,
    onConflicts: setCanvasConflict,
  });

  // A resumed canvas (?resume=<id>) binds that draft; useCanvasHydration reads it
  // fresh. ensureDraft's own ?resume write finds the id already bound.
  useEffect(() => {
    if (!resumeDraftId || draftSync.boatId === resumeDraftId) return;
    draftSync.setBoatId(resumeDraftId);
    setAutoSaveEnabled(true);
  }, [resumeDraftId, draftSync]);

  // #1177 (spec §3.3) — the dealer chose per field in "Changed elsewhere": the
  // choices go into the form over the fresh boat and are saved. Closing the dialog
  // keeps the dealer's values (its defaults, decision D45): autosave cannot leave
  // a save hanging.
  const resolveCanvasConflict = useCallback(
    (choices: Record<string, ConflictChoice>) => {
      if (!canvasConflict) return;
      const { fresh, merged, conflicts, lostRows } = canvasConflict;
      setCanvasConflict(null);
      hydration.reconcile(fresh, applyConflictChoices(merged, conflicts, choices));
      autoSave
        .saveNow()
        .then((outcome) => {
          // S4-26 — rows the dealer edited that were removed elsewhere (found by the
          // rebase that raised this dialog) are reported once the choice lands, as
          // the boat page does (#1268). The save's own lostRows are reported by
          // useCanvasAutoSave; another conflict carries these into its dialog.
          if (outcome?.kind === 'saved' || outcome?.kind === 'nothing_to_save') {
            for (const row of lostRows) {
              toast.error(`${describeLostRow(row)} was removed elsewhere; your changes to it were not saved.`);
            }
          } else if (outcome?.kind === 'conflicts' && lostRows.length > 0) {
            setCanvasConflict((next) => (next ? { ...next, lostRows: [...lostRows, ...next.lostRows] } : next));
          }
        })
        .catch(() => toast.error('Failed to save your changes. Please try again.'));
    },
    [canvasConflict, hydration, autoSave],
  );

  // #1178 — what the fields show beyond their values: the saved value the merge
  // wrote while the dealer was editing that field, and the review flags (#1179
  // photo suggestions and conflicts, #1188 HIN/VIN doubts), decided from the field.
  // The field-sources projection is copied into `_provenance` there (a field is
  // skipped only while it holds an unsaved change).
  const fieldReview = useCanvasFieldReview({
    methods,
    boatId: draftSync.boatId ?? null,
    fieldSources,
    hydration,
  });

  const { serverValues, dismissServerValue } = hydration;
  const review = useMemo<CanvasReviewValue>(
    () => ({
      serverValues,
      takeServerValue: (field) => {
        const value = serverValues[field];
        if (value === undefined) return;
        methods.setValue(field as never, toFormScalar(field as CanvasScalarKey, value) as never, { shouldDirty: true });
        dismissServerValue(field);
      },
      keepMine: dismissServerValue,
      acceptSuggestion: fieldReview.acceptSuggestion,
      dismissFlag: fieldReview.dismissFlag,
      pickSource: fieldReview.pickSource,
      hiddenFlagIds: fieldReview.hiddenFlagIds,
      photoUrl: (mediaId) => {
        const image = committedImages?.find((img) => String(img.id) === String(mediaId));
        return image ? (image.conversions?.card ?? image.url) : null;
      },
      notImportedOptions: hydration.notImportedOptions,
    }),
    [serverValues, dismissServerValue, methods, fieldReview.acceptSuggestion, fieldReview.dismissFlag, fieldReview.pickSource, fieldReview.hiddenFlagIds, committedImages, hydration.notImportedOptions],
  );



  // Footer action orchestration — mirrors AddBoatWizard's handleCreate +
  // handleSaveDraft pattern. Extracted into a hook so it can be unit-tested
  // in isolation without rendering the full canvas.
  const { handleCreate, handleSaveDraft, isSubmitting } = useCanvasBoatActions({
    methods,
    draftSync,
    saveChanges: autoSave.saveNow,
    mediaUpload,
    createBoat,
    createPendingVocabularyTerms,
    onOpenChange,
    onSuccess,
    setAutoSaveEnabled,
  });

  // Phase 45.5.1 (CANVAS-08, D-2) — silent auto-draft for the inline hero flow.
  // The old AddBoatWizard auto-creates a draft on first photo upload; the canvas
  // hero must feel the same — drop a factory packet and the boat is created for
  // you (no "Save draft first"). A factory packet / Certificate of Origin is a
  // first-sale document, so the boat is seeded condition:'new' (D-2). Per the
  // backend StoreInventoryBoatRequest, `condition` is the ONLY hard-required
  // field for a status_id=Draft payload (manufacturer/model/model_year/our_price
  // are all relaxed for drafts), so the rest may stay at the form's current
  // (blank/default) values — the packet fills them.
  //
  // Idempotent: if a boat already exists (resume, or a prior drop) it returns
  // that id immediately and NEVER double-creates. Mirrors
  // useCanvasBoatActions.handleSaveDraft's no-draft branch exactly:
  // createDraft → loadExisting (re-hydrates the committed gallery while the
  // canvas stays mounted) → enable autosave.
  // Fail-soft: a thrown createDraft (e.g. "Draft status not yet loaded") toasts
  // and returns null so the caller skips commit/extract instead of crashing.
  const ensureDraft = useCallback(async (): Promise<string | null> => {
    if (draftSync.boatId) {
      return draftSync.boatId;
    }
    try {
      const values = methods.getValues();
      const result = await draftSync.createDraft({
        // WR-06 (45.5.3 review): honor the dealer's explicit Condition choice.
        // The hero drop is reachable on a blank-new canvas where the dealer may
        // have already toggled Condition = Used via ConditionRow; hard-coding
        // 'new' here silently overwrote that on the persisted draft (the visible
        // form control then contradicted the saved value until a later autosave,
        // if any, corrected it). Fall back to 'new' only when unset — a factory
        // packet is still a first-sale document by default (D-2).
        condition: values.condition || 'new',
        manufacturer: values.manufacturer ?? '',
        model: values.model ?? '',
        // model_year is the form's NUMBER value (WIZARD_DEFAULT_VALUES seeds it
        // to new Date().getFullYear()); DraftBasicInfo.model_year is typed
        // `number` and must NEVER be an empty string — mirror handleSaveDraft.
        model_year: values.model_year,
      });

      // Phase 45.5.3 (review) — committed DISPLAY is now query-backed, so seed it
      // by invalidating the boat-media query: useBoatMedia(boatId) was disabled
      // (boatId was null) until setBoatId ran inside createDraft, so its cache is
      // empty. Invalidating boatMediaKeys.detail(result.id) triggers the first
      // fetch so the draft's images appear in the query-backed committed grid.
      //
      // BUG FIX (single source of truth): the prior
      // mediaUpload.loadExisting(result.images, result.primary_image) call is
      // REMOVED. It would create a THIRD, unreconciled committed-media snapshot
      // whose internal featuredIndex could go stale after a query-backed
      // committed delete, reintroducing the dual source of truth the query model
      // exists to remove. The committed grid is sourced exclusively from
      // useBoatMedia / useDocuments now.
      if (result?.id) {
        queryClient.invalidateQueries({
          queryKey: boatMediaKeys.detail(result.id),
        });
      }
      setAutoSaveEnabled(true);

      // Phase 3B (Task 3.7) — durable Copilot session handle. The moment the
      // draft boatId is FIRST minted (this createDraft branch runs exactly once;
      // every later ensureDraft hits the `draftSync.boatId` early-return above),
      // write `?new=&resume={boatId}` with replace:true. This reuses the
      // EXISTING resume hydration path: a refresh/return re-enters the canvas via
      // `?resume={boatId}` → the draft hydrates AND useCopilotSession replays the
      // boat's persisted Copilot timeline. replace (not push) so the browser Back
      // button doesn't land on the pre-mint blank-new URL.
      if (result?.id) {
        setSearchParams(
          (prev) => {
            prev.set('new', '');
            prev.set('resume', String(result.id));
            return prev;
          },
          { replace: true },
        );
      }

      // draftSync.createDraft already called setBoatId internally, so
      // draftSync.boatId (and the lifted upload's bound id) now point at the
      // new draft. Return the fresh id for the immediate commit/extract.
      return result.id;
    } catch (error) {
      // WR-04: preserve diagnostics. createDraft throws a specific, recoverable
      // 'Draft status not yet loaded — try again in a moment' when the statuses
      // catalog has not loaded yet; a bare catch discarded both the message and
      // the error object, making that transient race indistinguishable from a
      // real server failure and leaving nothing in the console (the sibling
      // useCanvasBoatActions.handleCreate logs its error). The user-facing
      // "try again in a moment" toast copy already matches that race's remedy.
      console.error('ensureDraft failed:', error);
      toast.error('Could not start your listing. Please try again in a moment.');
      return null;
    }
  }, [draftSync, methods, setAutoSaveEnabled, queryClient, setSearchParams]);

  // Review I7 — a change still inside the 5 s debounce is saved before the canvas
  // closes; a rejected save (field errors, the conflict dialog) keeps it open.
  // `stale` closes (re-review N5): its toast tells the dealer to close the canvas
  // and open it again, which re-reads the boat. Closing by the sheet's own X,
  // Escape or a route change is covered by the autosave hook's unmount save.
  const closeCanvas = useCallback(async () => {
    const outcome = await autoSave.saveNow().catch(() => null);
    if (outcome && (outcome.kind === 'invalid' || outcome.kind === 'conflicts')) return;
    onOpenChange(false);
  }, [autoSave, onOpenChange]);

  return (
    // Match the legacy AddBoatWizard wrapper exactly: a single flex-col
    // sized to fill the inset-5 sheet (h = 100dvh - 2.5rem). Header and
    // footer take their natural height; the body uses flex-1 + min-h-0
    // so it fills the remaining space without manual pixel math.
    <CopilotSessionProvider tenantId={tenantId} boatId={draftSync.boatId ?? null}>
    <div className="flex flex-col h-[calc(100dvh-2.5rem)]">
      <SheetHeader className="border-b py-3 px-5 border-border">
        <SheetTitle className="text-sm font-medium">Add New Boat</SheetTitle>
      </SheetHeader>
    <div
      className={cn(
        'grid flex-1 min-h-0',
        // Phase 45.5.3 (CANVAS-16) — the third (copilot) track follows the
        // lifted collapse boolean on desktop: 340px expanded → 48px rail
        // collapsed. No more dead column. Tablet/phone reflow is unchanged.
        //
        // Phase 45.5.3 (Copilot smooth-collapse) — animate grid-template-columns
        // so the third track (340↔48) and the `1fr` main column move together as
        // ONE fluid motion instead of snapping. Applied DESKTOP-ONLY: the two
        // desktop templates share the same 3-track shape
        // (`200px 1fr 340px` → `200px 1fr 48px`), which modern engines interpolate
        // track-by-track — `1fr` grows rightward as the third track shrinks
        // (Chrome 107+/Firefox/Safari 16+). The tablet/phone `grid-cols-1` reflow
        // does NOT carry the transition (a 3-track→1-track change can't
        // interpolate, and a layout reflow on resize must not animate).
        // `motion-reduce:transition-none` honors prefers-reduced-motion.
        isDesktop
          ? cn(
              'transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none',
              copilotCollapsed
                ? 'grid-cols-[200px_1fr_48px]'
                : 'grid-cols-[200px_1fr_340px]',
            )
          : 'grid-cols-1 grid-rows-[auto_1fr]',
      )}
      role="main"
      aria-label="Live-Fill Canvas"
    >
      {/* Left: section nav — sticky 200px on desktop, dropdown on tablet/phone */}
      <CanvasSectionNav activeSection={activeSection} onJumpTo={jumpTo} />

      {/* Center: form canvas.
          Phase 45.5.3 (refresh) — responsive: the content fills the center
          track and FLOWS with the rail collapse/expand (the grid container's
          `transition-[grid-template-columns]` animates the reflow). Within the
          1320px modal the collapsed center track is ~1072px, so `max-w-[1100px]`
          lets the content fill it in both rail states (the 1100 ceiling only
          guards line-length if the modal is ever wider). Horizontal padding is
          responsive (px-4 → sm:px-6 → lg:px-8) so the form breathes on smaller
          desktops without overflowing. */}
      <main className="overflow-y-auto px-4 py-10 sm:px-6 lg:px-8 mx-auto w-full max-w-[1100px]">
        {showHeroIngest && (
          <CanvasHeroIngest
            draftId={draftSync.boatId}
            upload={upload}
            ensureDraft={ensureDraft}
            onConfirmExtract={(boatId) => factoryPacket.trigger(boatId)}
            // Phase 45.5.3 (review) — refresh the query-backed committed grid
            // after a successful inline commit. The staged combined-upload POST
            // holds image items in inventory_pending_images (#1219), so invalidating
            // the boat-media query makes them appear in the grid below. This is the
            // staged-commit half of the missing-invalidation fix (the createDraft
            // half lives in ensureDraft).
            //
            // BUG FIX (staged↔committed dedup follow-on): useCombinedUpload.commit
            // now CLEARS its staged items on success, so the committed cards must
            // come from the queries. Two consequences handled here:
            //   1. NO-FLASH SEED: the boat-media refetch is async, so a first drop
            //      would render zero cards for a beat (the grid returns null when
            //      both sources are empty). Seed boatMediaKeys.detail from
            //      result.images BEFORE the background invalidate so the committed
            //      image cards appear the instant staged clears. The combined-
            //      upload payload has no `conversions` (only `url`), which
            //      mapQueryImageToCommittedCard tolerates (full-res until the
            //      refetch reconciles to card-size). Preserve the {images, videos,
            //      pending_images} shape exactly — drop none of the three keys or
            //      useBoatMedia consumers break (#1219 added pending_images).
            //   2. DOCUMENTS INVALIDATION (BLOCKER): a documents-only commit clears
            //      the staged doc card but the committed-document grid is sourced
            //      from useDocuments → documentKeys. Without invalidating that
            //      family the committed doc would vanish mid-extraction (staged
            //      gone, query never refetched). Invalidate documentKeys.lists()
            //      alongside the media query so a doc-only packet still shows its
            //      card.
            onCommitted={(boatId, result?: CombinedUploadResult) => {
              if (result?.images?.length || result?.pending_images?.length) {
                queryClient.setQueryData(
                  boatMediaKeys.detail(boatId),
                  (prev) => ({
                    images: result.images,
                    videos:
                      (prev as BoatMediaResponse | undefined)?.videos ?? [],
                    // #1219 — held canvas images, so their cards don't flash away.
                    pending_images: result.pending_images ?? [],
                  }),
                );
              }
              queryClient.invalidateQueries({
                queryKey: boatMediaKeys.detail(boatId),
              });
              queryClient.invalidateQueries({
                queryKey: documentKeys.lists(),
              });
            }}
            isReading={factoryPacket.isReading || hydration.isRunActive}
            // Phase 45.5.3 (review) — the COMMITTED gallery is now derived from
            // the live useBoatMedia(draftSync.boatId) query (the SINGLE source of
            // truth, exactly like v1's BoatMediaManager) rather than a local
            // useUnifiedMediaUpload snapshot. Saved images render in the hero's
            // MediaCard grid and can be ⭐ set-featured / deleted; because the
            // list is query-backed, those mutations' cache invalidation drives
            // the grid — it SELF-HEALS on the refetch with NO hand-reconciliation
            // (this replaces the removed CR-01/CR-02 band-aid). The primary is
            // images[0] per BoatMediaManager, passed as committedPrimaryImageId.
            committedItems={committedItems}
            committedPrimaryImageId={committedPrimaryImageId}
            // #1219 — held canvas uploads from the same query (authenticated previews).
            pendingImages={boatMedia.data?.pending_images ?? []}
            // Phase 45.5.3 (committed DOCUMENTS on resume) — the factory-packet
            // documents from useDocuments(draftSync.boatId), rendered in the SAME
            // grid so a resumed draft's COO/invoice/BOL files are visible. Delete
            // fires the documents destroy mutation, whose onSuccess invalidates
            // documentKeys.lists() so the query refetch self-heals the card away
            // (the same single-source-of-truth contract as committed images).
            committedDocuments={committedDocuments}
            onDeleteDocument={(mediaId) =>
              destroyDocument.mutate({ mediaId })
            }
          />
        )}

        <div className="mt-8 pb-8">
          {/* Phase 45.5.2 (CANVAS-10) — mediaUpload is no longer passed: the
              separate Photos upload section was removed from CanvasFormSections.
              The mediaUpload hook is still owned here for the footer actions
              (useCanvasBoatActions Save-draft / Create: clear / loadExisting). Phase 45.5.3 (review): committed DISPLAY no longer flows
              through it — that comes from the useBoatMedia query above. */}
          <CanvasReviewContext.Provider value={review}>
            <CanvasFormSections
              wizardAI={wizardAI}
              dealershipSettings={dealershipSettings}
              sectionIds={CANVAS_SECTION_IDS}
              boatId={draftSync.boatId ?? null}
              marketBand={marketBand}
            />
          </CanvasReviewContext.Provider>
        </div>
      </main>

      {/* Right: copilot column — Plan 43-06.
          CopilotSidebar handles desktop-expanded (340px), desktop-collapsed
          (48px rail), AND tablet/phone (bottom-sheet FAB) variants
          internally. The {isDesktop && ...} guard is intentionally GONE —
          on tablet the FAB renders out of grid flow (position: fixed).
      */}
      <CopilotSidebar
        tenantId={tenantId}
        inventoryItemId={draftSync.boatId ?? null}
        onJumpToField={jumpTo}
        // Phase 3B (Task 3.6) — the unified CopilotFeed inside the sidebar owns
        // the whole Copilot session (live rows + narrative + terminal) via
        // useCopilotSession(tenantId, boatId). The canvas no longer threads a
        // jobId, the wizardAI CTA handle, the post-Comparables CTA props, or the
        // extractionComplete flag — those fed the retired panel/CTAs/agent-steps
        // trail. The terminal "Review your listing" CTA (now in the feed) still
        // jumps to the first canvas section.
        onReviewExtraction={() => jumpTo(CANVAS_SECTION_IDS[0])}
        // Phase 45.5.3 (CANVAS-16) — the lifted collapse boolean. CopilotSidebar
        // renders the rail/expanded view from this prop; the toggle flips the
        // state here, which ALSO shrinks the grid's third track above (340↔48).
        // LiveFillCanvas is now the single localStorage owner for the key.
        collapsed={copilotCollapsed}
        onToggleCollapse={() => setCopilotCollapsed((c) => !c)}
      />
    </div>
    <ChangedElsewhereDialog
      open={canvasConflict !== null}
      conflicts={canvasConflict?.conflicts ?? []}
      merged={canvasConflict?.merged}
      onResolve={resolveCanvasConflict}
      // D45 — closing keeps the dealer's values (the defaults shown), so the button says so.
      cancelLabel="Keep mine"
      onCancel={() => resolveCanvasConflict({})}
    />
    {/* Footer: spans full sheet width, anchored at the bottom of the sheet
        (NOT sticky-inside-scroll). Matches the legacy AddBoatWizard
        SheetFooter shape verbatim so dealers see the same chrome on
        both wizards. */}
    <SheetFooter className="flex-row items-center border-t py-2.5 px-5 border-border gap-2.5">
      <div className="flex-1" />
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={() => void closeCanvas()}>
          Close
        </Button>
        <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={draftSync.isCreating}>
          Save draft
        </Button>
        <Button variant="primary" size="sm" onClick={handleCreate} disabled={isCreating || isSubmitting}>
          Create boat
        </Button>
      </div>
    </SheetFooter>
    </div>
    </CopilotSessionProvider>
  );
}
